import React, { useState, useEffect } from 'react';
import { Modal, Slider, InputNumber, message } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import type { CopyPortfolio } from '../../../../types/copyTrading';
import { useCopyPortfolioStore } from '../../../hooks/copyTrading/copyPortfolioStore';
import { formatDecimal } from '@utils/formatDecimal';
import { getBigNumber } from '@utils/getBigNumber';

interface FollowPortfolioModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  portfolio: CopyPortfolio | null;
}

const MAX_ALLOCATED_AMOUNT = 1000000;

export const FollowPortfolioModal: React.FC<FollowPortfolioModalProps> = ({
  open,
  setOpen,
  portfolio,
}) => {
  const { t } = useTranslation();
  const { startFollow, isLoading } = useCopyPortfolioStore();

  // Form State
  const [copyRatio, setCopyRatio] = useState<number>(25); // 25% 기본값
  const [allocatedAmount, setAllocatedAmount] = useState<number>(100);

  // Reset form when modal opens
  useEffect(() => {
    if (open && portfolio) {
      setCopyRatio(25);
      setAllocatedAmount(portfolio.minCopyAmount || 100);
    }
  }, [open, portfolio]);

  if (!portfolio) {
    return null;
  }

  const handleCancel = () => {
    setOpen(false);
  };

  const handleConfirm = async () => {
    // Validation
    if (allocatedAmount < (portfolio.minCopyAmount || 100)) {
      message.error(
        t('minCopyAmountError', {
          amount: formatDecimal(portfolio.minCopyAmount || 100, 2),
        })
      );
      return;
    }

    if (allocatedAmount > MAX_ALLOCATED_AMOUNT) {
      message.error(
        t('maxAllocatedAmountError', {
          amount: formatDecimal(MAX_ALLOCATED_AMOUNT, 2),
        })
      );
      return;
    }

    if (copyRatio < 0 || copyRatio > 100) {
      message.error(t('copyRatioRangeError'));
      return;
    }

    try {
      await startFollow({
        portfolioId: portfolio.id,
        copyRatio: getBigNumber(copyRatio).dividedBy(100).toNumber(), // Convert percentage to decimal
        allocatedAmount,
      });

      message.success(t('followStartSuccess'));
      setOpen(false);
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.error?.message ||
        error?.message ||
        t('followStartFailed');

      // 최대 팔로워 수 초과 에러 체크
      if (errorMsg.includes('maximum limit') && errorMsg.includes('followers')) {
        message.error({
          content: t('maxFollowersReachedError', {
            max: portfolio.maxFollowers || 5,
          }),
          duration: 5,
        });
        return;
      }

      // 잔액 부족 에러 체크
      if (errorMsg.includes('Insufficient balance')) {
        message.error({
          content: t('insufficientBalanceError'),
          duration: 5,
        });
        return;
      }

      // 자기 자신 팔로우 에러 체크
      if (errorMsg.includes('You cannot follow your own portfolio')) {
        message.error(t('cantFollowYourOwnPortfolio'));
        return;
      }

      // 이미 팔로우 중인 경우
      if (errorMsg.includes('already following')) {
        message.error(t('alreadyFollowingError'));
        return;
      }

      // 최소 할당 금액 에러 체크 (백엔드에서 1000 USDT 이상 요구)
      if (errorMsg.includes('Allocated amount must be at least')) {
        // 에러 메시지에서 금액 추출 (예: "Allocated amount must be at least 1000 USDT")
        const amountMatch = errorMsg.match(/at least (\d+)/);
        const minAmount = amountMatch ? amountMatch[1] : '1000';
        message.error({
          content: t('minAllocatedAmountError', { amount: minAmount }),
          duration: 5,
        });
        return;
      }

      // 기타 에러
      console.error('[FollowPortfolioModal] Error:', errorMsg);
      message.error(t('followStartFailed'));
    }
  };

  return (
    <StyledModal
      open={open}
      width={424}
      centered={true}
      closable={false}
      onCancel={handleCancel}
      footer={null}
      maskClosable={false}
    >
      <ModalContainer>
        {/* Header */}
        <ModalHeader>
          <ModalTitle>
            {t('followPortfolioTitle', { username: portfolio.leaderUsername })}
          </ModalTitle>
          <CloseButton onClick={handleCancel}>
            <CloseIcon
              src="/assets/newHome/copy-trading/close-icon-follow.svg"
              alt="Close"
            />
          </CloseButton>
        </ModalHeader>

        {/* Content */}
        <ModalContent>
          {/* Portfolio Info Card */}
          <InfoCard>
            <InfoRow>
              <InfoLabel>{t('leader')}</InfoLabel>
              <InfoValue>{portfolio.leaderUsername}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('portfolio')}</InfoLabel>
              <InfoValue>{portfolio.portfolioName}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('profitShareRate')}</InfoLabel>
              <InfoValueGreen>
                {formatDecimal(portfolio.profitShareRate || 0, 0)}%
              </InfoValueGreen>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('currentFollowers')}</InfoLabel>
              <InfoValueGreen>
                {portfolio.currentFollowers || 0}/{portfolio.maxFollowers || 5}
              </InfoValueGreen>
            </InfoRow>
          </InfoCard>

          {/* Copy Ratio */}
          <FormSection>
            <FormLabel>{t('copyRatio')}</FormLabel>
            <FormDesc>{t('copyRatioDesc')}</FormDesc>
            <SliderContainer>
              <div
                style={{
                  position: 'relative',
                  boxSizing: 'border-box',
                  padding: '12px 17px 0 14px',
                  marginBottom: '8px',
                  width: '100%',
                }}
              >
                <Slider
                  min={0}
                  max={100}
                  value={copyRatio}
                  onChange={setCopyRatio}
                  marks={{
                    0: {
                      label: (
                        <div
                          style={{
                            fontSize: 12,
                            color: '#B3B5BD',
                            fontFamily: 'NanumSquareRound',
                            letterSpacing: -0.36,
                            position: 'relative',
                            left: 'calc(50% - 4px)',
                            top: '4px',
                          }}
                        >
                          0%
                        </div>
                      ),
                    },
                    25: {
                      label: <strong style={{ fontSize: 11 }}>{''}</strong>,
                    },
                    50: {
                      label: <strong style={{ fontSize: 11 }}>{''}</strong>,
                    },
                    75: {
                      label: <strong style={{ fontSize: 11 }}>{''}</strong>,
                    },
                    100: {
                      label: (
                        <div
                          style={{
                            fontSize: 12,
                            color: '#B3B5BD',
                            fontFamily: 'NanumSquareRound',
                            letterSpacing: -0.36,
                            position: 'relative',
                            right: 'calc(50% - 4px)',
                            top: '4px',
                          }}
                        >
                          100%
                        </div>
                      ),
                    },
                  }}
                  styles={{
                    rail: {
                      background: '#2A2C2E',
                      height: 3,
                    },
                    track: {
                      background: '#07D993',
                      height: 3,
                    },
                    handle: {
                      border: '1px solid #07D993',
                      background: '#07D993',
                    },
                  }}
                />
              </div>
              <SliderCurrentValue>{copyRatio}%</SliderCurrentValue>
            </SliderContainer>
          </FormSection>

          {/* Allocated Amount */}
          <FormSection>
            <FormLabel>{t('allocatedAmountUSDT')}</FormLabel>
            <FormDesc>
              {t('minAmount', {
                amount: formatDecimal(portfolio.minCopyAmount || 100, 0),
              })}
            </FormDesc>
            <AmountInput
              value={allocatedAmount}
              onChange={(value) => setAllocatedAmount(value as number)}
              formatter={(value) => `${value} USDT`}
              parser={(value) => value?.replace(' USDT', '') as any}
            />
          </FormSection>

          {/* Divider */}
          <Divider />

          {/* Summary Card */}
          <SummaryCard>
            <SummaryRow>
              <SummaryLabel>{t('copyRatio')}</SummaryLabel>
              <SummaryValue>{copyRatio}%</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{t('allocatedAmount')}</SummaryLabel>
              <SummaryValue>
                {formatDecimal(allocatedAmount, 0)} USDT
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{t('profitShareRate')}</SummaryLabel>
              <SummaryValueGreen>
                {formatDecimal(portfolio.profitShareRate || 0, 0)}%
              </SummaryValueGreen>
            </SummaryRow>
          </SummaryCard>
        </ModalContent>

        {/* Notice Section */}
        <NoticeSection>
          <NoticeHeader>
            <InfoIcon
              src="/assets/newHome/copy-trading/info-icon.svg"
              alt="Info"
            />
            <NoticeTitle>{t('notice')}</NoticeTitle>
          </NoticeHeader>
          <NoticeText>• {t('followNotice1')}</NoticeText>
          <NoticeText>• {t('followNotice2')}</NoticeText>
          <NoticeText>• {t('followNotice3')}</NoticeText>
        </NoticeSection>

        {/* Footer */}
        <ModalFooter>
          <CancelButton onClick={handleCancel} disabled={isLoading}>
            {t('cancel')}
          </CancelButton>
          <SubmitButton onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? t('starting') : t('startFollowing')}
          </SubmitButton>
        </ModalFooter>
      </ModalContainer>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: #0c2323;
    border-radius: 16px;
    padding: 0;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
  }
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 1001px;
  height: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 40px 35px;
`;

const ModalTitle = styled.h2`
  font-family: 'Aggro', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.36px;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 0 35px;
  flex: 1;
  overflow-y: visible;
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const InfoLabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const InfoValue = styled.div`
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
`;

const InfoValueGreen = styled.div`
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #07d993;
  letter-spacing: -0.28px;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FormLabel = styled.label`
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
`;

const FormDesc = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SliderCurrentValue = styled.div`
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #07d993;
  letter-spacing: -0.32px;
  text-align: center;
`;


const AmountInput = styled(InputNumber)`
  width: 100%;
  height: 50px;

  .ant-input-number-input {
    height: 50px !important;
    padding: 10px 20px !important;
    background: transparent !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 12px !important;
    color: #ffffff !important;
    font-family: 'NanumSquareRound', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    letter-spacing: -0.28px !important;
  }

  .ant-input-number-handler-wrap {
    display: none;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #353535;
  margin: 0;
`;

const SummaryCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const SummaryLabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const SummaryValue = styled.div`
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
`;

const SummaryValueGreen = styled.div`
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #07d993;
  letter-spacing: -0.28px;
`;

const NoticeSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  margin: 32px 35px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NoticeHeader = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
  margin-bottom: 8px;
`;

const InfoIcon = styled.img`
  width: 20px;
  height: 20px;
  transform: scaleY(-1);
`;

const NoticeTitle = styled.div`
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ff8a00;
  letter-spacing: -0.32px;
`;

const NoticeText = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
  line-height: 18px;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 30px 35px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const CancelButton = styled.button`
  flex: 1;
  height: 48px;
  padding: 20px 40px;
  background: transparent;
  border: 1px solid #353535;
  border-radius: 12px;
  color: #ffffff;
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  height: 48px;
  padding: 20px 40px;
  background: #07d993;
  border: none;
  border-radius: 12px;
  color: #0d1117;
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
